import { useState, FormEvent, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSharedState } from "state";
import PageLayout from "components/layout/page-layout";
import Select from "react-select";
import { addBudgetRequest, updateBudgetRequest, getArticles } from "api/budget_request";
import moment from "moment";
import { ArticlesResponse, BudgetTableItem, emptyBudgetRow } from "types/budget";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import { getLevelTreeStructure } from "api/level";
import FormattedNumberInput from "components/input/formatted-number";
import { LevelDefault, LevelStructureType, LevelType } from "types/dict";
import DangerModal from "components/modal/danger";

type FormErrors = Record<string, boolean>;

type FormRecords = {
  budget_type_id: number;
  year: number;
  company_id: number;
  department_id: number;
};

type SelectOption = {
  value: number;
  label: string;
  disabled?: boolean;
};

const REQUIRED_FIELDS = ["budget_type_id", "year", "company_id", "department_id"] as const;

const getInputErrorMessage = (fieldId: string) =>
  fieldId === "file" ? "Добавьте договор" : "Введите корректное значение";

const VAT_OPTIONS = [
  {
    value: "true",
    label: "Да",
    disabled: false,
  },
  {
    value: "false",
    label: "Нет",
    disabled: false,
  },
];

export default function NewBudget() {
  const {
    departments,
    getDepartmentsData,
    companies,
    getCompaniesData,
    contractors,
    getContractorsData,
    levels,
    levelRoots,
    getLevelRootsData,
    codes,
    getCodesData,
    budgetRequests,
    isAdmin,
    getBudgetRequestsData,
    searchQuery,
    handleSearch,
    notify,
  } = useSharedState();
  const { budgetId, locationName } = useParams();
  const navigate = useNavigate();

  // Store available levels for each budget row and each level
  const [availableLevelsByRow, setAvailableLevelsByRow] = useState<{ [rowIdx: number]: LevelType[][] }>({}); // Store available levels for each row and level
  const [treeStructure, setTreeStructure] = useState<LevelStructureType | undefined>(undefined);

  const [formRecords, setFormRecords] = useState<FormRecords>({
    budget_type_id: 0,
    year: moment().year(),
    company_id: 0,
    department_id: 0,
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [requestTable, setRequestTable] = useState<BudgetTableItem[]>([emptyBudgetRow]);

  const [articleOptions, setArticleOptions] = useState<{ label: string; value: string }[][]>([]);
  const [articleLoading, setArticleLoading] = useState<boolean[]>([]);
  const [articleInput, setArticleInput] = useState<string[]>([]);
  const articleTimeouts = useRef<(number | null)[]>([]);

  const deleteBudgetDanger = "Вы действительно хотите удалить бюджет?";

  const mainLevel = useMemo(() => {
    return levelRoots.find((lvl) => lvl.budget_type_id === formRecords.budget_type_id);
  }, [levelRoots, formRecords.budget_type_id]);

  const codeOptions = useMemo(() => {
    return codes.map((lvl) => ({
      value: lvl.code_it_id,
      label: lvl.code_it,
      disabled: false,
    }));
  }, [codes]);

  const contractorOptions = useMemo(() => {
    return contractors.map((lvl) => ({
      value: lvl.contragent_id,
      label: lvl.contragent,
      disabled: false,
    }));
  }, [contractors]);

  const totalSum = useMemo(() => {
    return requestTable.reduce((sum, item) => sum + (item.row_total || 0), 0);
  }, [requestTable]);

  useEffect(() => {
    getDepartmentsData();
    getCompaniesData();
    getContractorsData();
    getLevelRootsData();
    getCodesData();
  }, []);

  useEffect(() => {
    if (formRecords.budget_type_id) {
      // Reset budget line rows when budget type changes
      setRequestTable((prev) =>
        prev.map((row) => ({
          ...row,
          budget_line_rows: [],
        }))
      );

      getLevelTreeStructure(formRecords.budget_type_id)
        .then((tree: LevelStructureType) => {
          setTreeStructure(tree);
          requestTable.forEach((_, index) => {
            setAvailableLevelsByRow((prev) => ({
              ...prev,
              [index]: [[...(tree?.children || [])]],
            }));
          });
        })
        .catch((error) => {
          console.error("Error fetching level structure:", error);
          notify(String(error), "danger", "Ошибка при загрузке структуры уровня");
        });
    }
  }, [formRecords.budget_type_id]);

  useEffect(() => {
    if (budgetId) {
      const budget = budgetRequests.budget_requests.find((t) => t.budget_request_id === Number(budgetId));
      if (budget) {
        setFormRecords({
          budget_type_id: levelRoots.find((lvl) => lvl.budget_type === budget.budget_type)?.budget_type_id || 0,
          year: budget.year,
          company_id: companies.find((c) => c.company === budget.company)?.company_id || 0,
          department_id: departments.find((c) => c.department === budget.department)?.department_id || 0,
        });
        setRequestTable(
          budget.budget_request_details.map((detail) => ({
            ...detail,
            budget_line_rows: detail.budget_line_rows.map(
              (row) => treeStructure?.nodes.find((lvl) => lvl.budget_type_id === row.budget_type_id) || LevelDefault
            ),
          }))
        );
      } else {
        notify("Бюджет не найден", "danger");
        navigate(-1);
      }
    }
  }, [budgetId, budgetRequests, levelRoots, companies, departments, treeStructure]);

  const budgetFields = [
    {
      id: "budget_type_id",
      title: "Название бюджета",
      type: "select",
      placeholder: "Выберите бюджет",
      options: levelRoots.map((lvl) => ({
        label: lvl.budget_type!,
        value: lvl.budget_type_id!,
        disabled: !!!lvl.status,
      })),
    },
    {
      id: "year",
      title: "Год",
      type: "number",
      placeholder: undefined,
      options: undefined,
    },
    {
      id: "company_id",
      title: "Юр. лицо",
      type: "select",
      placeholder: "Выберите юр. лицо",
      options: companies
        .filter((com) => com.status)
        .map((com) => ({
          label: com.company,
          value: com.company_id,
        })),
    },
    {
      id: "department_id",
      title: "Отдел",
      type: "select",
      placeholder: "Выберите отдел",
      options: departments
        .filter((dep) => dep.status)
        .map((dep) => ({
          label: dep.department,
          value: dep.department_id,
        })),
    },
  ] as const;

  const validateForm = () => {
    const newErrors: FormErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      const value = formRecords[field];
      if (Array.isArray(value) ? value.length === 0 : !value || !value?.toString().trim()) {
        newErrors[field] = true;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateRequestTable = () => {
    return requestTable.every((row) => {
      const hasRequiredFields =
        row.article &&
        row.code_it_id &&
        row.contragent_id &&
        row.quantity !== null &&
        row.quantity !== undefined &&
        row.mon_1 !== null &&
        row.mon_1 !== undefined &&
        row.mon_2 !== null &&
        row.mon_2 !== undefined &&
        row.mon_3 !== null &&
        row.mon_3 !== undefined &&
        row.mon_4 !== null &&
        row.mon_4 !== undefined &&
        row.mon_5 !== null &&
        row.mon_5 !== undefined &&
        row.mon_6 !== null &&
        row.mon_6 !== undefined &&
        row.mon_7 !== null &&
        row.mon_7 !== undefined &&
        row.mon_8 !== null &&
        row.mon_8 !== undefined &&
        row.mon_9 !== null &&
        row.mon_9 !== undefined &&
        row.mon_10 !== null &&
        row.mon_10 !== undefined &&
        row.mon_11 !== null &&
        row.mon_11 !== undefined &&
        row.mon_12 !== null &&
        row.mon_12 !== undefined;
      const hasAllLevels = mainLevel?.max_levels ? row.budget_line_rows?.length === mainLevel.max_levels : true;
      return hasRequiredFields && hasAllLevels;
    });
  };

  type InputChangeEvent = React.ChangeEvent<HTMLInputElement>;
  type SelectChangeEvent = readonly SelectOption[] | null;
  type SingleSelectEvent = SelectOption | null;

  const handleInputChange = (e: InputChangeEvent | SelectChangeEvent | SingleSelectEvent, fieldId?: string) => {
    if (fieldId === "budget_type_id") {
      setRequestTable((prev) => {
        const newTable = [...prev];

        newTable.forEach((r) => {
          r.budget_line_rows = [];
        });

        return newTable;
      });
    }

    if (e && "target" in e) {
      // Handle input change
      const { name, value } = e.target;
      setFormRecords((prev) => ({ ...prev, [name]: name === "year" ? value.slice(0, 4) : value }));
      setErrors((prev) => ({ ...prev, [name]: false }));
    } else {
      // Handle select change
      // @ts-ignore
      const values = Array.isArray(e) ? e.map((option) => option.value) : e ? e.value : "";
      setFormRecords((prev) => ({ ...prev, [fieldId!]: values }));
      setErrors((prev) => ({ ...prev, [fieldId!]: false }));
    }
  };

  const updateTable = async () => {
    await getBudgetRequestsData();
    await handleSearch(searchQuery, undefined);
  };

  const submitBudget = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !validateRequestTable()) {
      notify("Заполните все поля", "danger");
      return;
    }

    setFormLoading(true);
    try {
      if (budgetId) {
        await updateBudgetRequest(Number(budgetId), {
          ...formRecords,
          budget_request_details: requestTable.map((row) => ({
            ...row,
            contragent_id: row.contragent_id || undefined,
          })),
        });
        notify("Бюджет обновлен", "success");
      } else {
        await addBudgetRequest({
          ...formRecords,
          budget_request_details: requestTable.map((row) => ({
            ...row,
            contragent_id: row.contragent_id || undefined,
          })),
        });
        notify("Бюджет создан", "success");
      }

      setFormSubmitted(true);
      await updateTable();
      navigate(-1);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", budgetId ? "Ошибка при обновлении бюджета" : "Ошибка при создании бюджета");
    } finally {
      setFormLoading(false);
    }
  };

  const handleBudgetDelete = async () => {
    setFormLoading(true);

    try {
      const budget = { ...budgetRequests.budget_requests.find((t) => t.budget_request_id === Number(budgetId)) };
      const payload = {
        ...budget,
        budget_type_id: budget.budget_request_id,
        company_id: companies.find((c) => c.company === budget.company)?.company_id || 0,
        department_id: departments.find((c) => c.department === budget.department)?.department_id || 0,
        status: false,
      };
      await updateBudgetRequest(Number(budgetId), payload);

      notify("Бюджет удалён", "success");
      setFormSubmitted(true);
      await updateTable();
      navigate(-1);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при удалении бюджета");
    } finally {
      setFormLoading(false);
    }
  };

  const handleAddRow = () => {
    const newIndex = requestTable.length;
    setRequestTable((prev) => [...prev, { ...emptyBudgetRow }]);

    if (formRecords.budget_type_id) {
      getLevelTreeStructure(formRecords.budget_type_id)
        .then((tree) => {
          setAvailableLevelsByRow((prev) => ({
            ...prev,
            [newIndex]: [[...(tree?.children || [])]],
          }));
        })
        .catch((error) => {
          console.error("Error fetching level structure:", error);
          notify(String(error), "danger", "Ошибка при загрузке структуры уровня");
        });
    }
  };

  const handleDeleteRow = (index: number) => {
    setRequestTable((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle article search and debounce
  const handleArticleInputChange = (inputValue: string, index: number) => {
    setArticleInput((prev) => {
      const arr = [...prev];
      arr[index] = inputValue;
      return arr;
    });
    if (articleTimeouts.current[index]) {
      clearTimeout(articleTimeouts.current[index]!);
    }
    if (inputValue.trim().length >= 3) {
      setArticleLoading((prev) => {
        const arr = [...prev];
        arr[index] = true;
        return arr;
      });
      articleTimeouts.current[index] = window.setTimeout(async () => {
        try {
          const res: ArticlesResponse = await getArticles(inputValue.trim());
          setArticleOptions((prev) => {
            const arr = [...prev];
            arr[index] = Array.isArray(res.articles) ? res.articles.map((a) => ({ label: a, value: a })) : [];
            return arr;
          });
        } catch {
          setArticleOptions((prev) => {
            const arr = [...prev];
            arr[index] = [];
            return arr;
          });
        } finally {
          setArticleLoading((prev) => {
            const arr = [...prev];
            arr[index] = false;
            return arr;
          });
        }
      }, 1000);
    } else {
      setArticleOptions((prev) => {
        const arr = [...prev];
        arr[index] = [];
        return arr;
      });
      setArticleLoading((prev) => {
        const arr = [...prev];
        arr[index] = false;
        return arr;
      });
    }
  };

  const handleTableChange = async (
    index: number,
    field: keyof BudgetTableItem,
    value: string | number | boolean | LevelType | null,
    lvl_num?: number
  ) => {
    setRequestTable((prev) => {
      const newTable = [...prev];
      newTable[index] = {
        ...newTable[index],
        [field]:
          field === "quantity" || field === "code_it_id" || field === "contragent_id"
            ? value === ""
              ? undefined
              : Number(value)
            : field === "vat"
            ? value === ""
              ? false
              : Boolean(value === "true" ? true : false)
            : field === "budget_line_rows"
            ? (() => {
                if (!lvl_num) return newTable[index].budget_line_rows || [];

                const existingRows = [...(newTable[index].budget_line_rows || [])].slice(0, lvl_num - 1);

                if (value && typeof value === "object" && !Array.isArray(value) && "budget_type" in value) {
                  const levelNode = value as LevelType;
                  existingRows[lvl_num - 1] = levelNode;

                  // Update available options for the next level from the children
                  if (levelNode.children && levelNode.children.length > 0) {
                    setAvailableLevelsByRow((prev) => {
                      const arr = prev[index] ? [...prev[index]] : [];
                      arr[lvl_num] = levelNode.children || [];
                      return {
                        ...prev,
                        [index]: arr.slice(0, lvl_num + 1),
                      };
                    });
                  }
                }

                return existingRows;
              })()
            : field === "article"
            ? value
            : value === ""
            ? undefined
            : Number(value),
      };

      // Recalculate row total if any month value changes
      if (field.startsWith("mon_")) {
        const months = Array.from({ length: 12 }, (_, i) => `mon_${i + 1}` as keyof BudgetTableItem);
        newTable[index].row_total = months.reduce((sum, month) => sum + ((newTable[index][month] as number) || 0), 0);
      }

      return newTable;
    });

    // After setting the requestTable
    if (field === "budget_line_rows" && typeof lvl_num === "number") {
      // When a level changes, get its tree structure
      const currentNode = availableLevelsByRow[index]?.[lvl_num - 1]?.find(
        (node) => node.budget_type === String(value)
      );

      if (currentNode) {
        try {
          // Get the tree structure for the selected level
          const tree = await getLevelTreeStructure(currentNode.budget_type_id!);

          setAvailableLevelsByRow((prev) => {
            const arr = prev[index] ? [...prev[index]] : [];
            // Set the next level's options to be the children of the selected level
            if (tree?.children) {
              arr[lvl_num] = tree.children;
            }
            // Remove all deeper levels after the current one
            return {
              ...prev,
              [index]: arr.slice(0, lvl_num + 1),
            };
          });
        } catch (error) {
          console.error("Error fetching level structure:", error);
          notify(String(error), "danger", "Ошибка при загрузке структуры уровня");
        }
      } else {
        // If no level is selected, clear this and all deeper levels
        setAvailableLevelsByRow((prev) => {
          const arr = prev[index] ? [...prev[index]] : [];
          return {
            ...prev,
            [index]: arr.slice(0, lvl_num),
          };
        });
      }
    }
  };

  const requestTableHeaders = [
    {
      header:
        !isAdmin && budgetId ? undefined : (
          <button
            type="button"
            title="Добавить строку"
            className="btn btn-icon btn-ghost-primary"
            onClick={handleAddRow}
          >
            <IconPlus size={16} />
          </button>
        ),
      required: false,
      narrow: true,
      wrap: false,
    },
    { header: "Наименование статьи", required: true, narrow: false, wrap: false },
    ...(mainLevel?.max_levels && mainLevel?.max_levels > 0
      ? Array.from({ length: mainLevel.max_levels }, (_, i) => ({
          header: `Уровень ${i + 1}`,
          required: true,
          narrow: false,
          wrap: false,
        }))
      : []),
    { header: "Код ИТ", required: true, narrow: false, wrap: true },
    { header: "Контрагент", required: true, narrow: false, wrap: true },
    { header: "НДС", required: true, narrow: false, wrap: false },
    { header: "Кол-во", required: true, narrow: false, wrap: false },
    { header: "Сумма", required: false, narrow: false, wrap: false },
    ...[
      "Январь",
      "Февраль",
      "Март",
      "Апрель",
      "Май",
      "Июнь",
      "Июль",
      "Август",
      "Сентябрь",
      "Октябрь",
      "Ноябрь",
      "Декабрь",
    ].map((month) => ({ header: month, required: true, narrow: false, wrap: false })),
  ];

  const renderField = ({ id, title, type, placeholder, options }: (typeof budgetFields)[number]) => {
    return (
      <div className={"col-lg-3"} key={id}>
        <div className="mb-3">
          <label className="form-label required">{title}</label>
          <div className="input-icon mb-3">
            {type === "number" ? (
              <input
                type="number"
                className={`form-control ${errors[id] ? "is-invalid" : ""}`}
                name={id}
                min={1990}
                step={1}
                value={formRecords[id]}
                onChange={handleInputChange}
                disabled={isFormLoading || !!budgetId}
              />
            ) : (
              <Select<SelectOption>
                classNamePrefix="react-select"
                name={id}
                placeholder={placeholder}
                value={options?.find((opt) => opt.value === formRecords[id])}
                onChange={(newValue) => handleInputChange(newValue, id)}
                isDisabled={isFormLoading || !!budgetId}
                options={options}
                isOptionDisabled={(option) => option.disabled || false}
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            )}

            <div className="invalid-feedback">{getInputErrorMessage(id)}</div>
            {isFormLoading && (
              <span className="input-icon-addon">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const newBudgetBody = (
    <div>
      <form id="budgetForm" className="row" onSubmit={submitBudget}>
        {budgetFields.map(renderField)}

        <div className="col-12 mb-3">
          <div className="d-flex gap-3">
            <label className="form-label required">Таблица бюджетов</label>
            <div className="alert alert-primary px-2 py-0" role="alert">
              <div className="text-secondary">Если данные для заполняемых ячеек отстутствуют - впишите в них "0"</div>
            </div>
          </div>
          <div className="card rounded-2xl">
            <div className="table-responsive rounded-2xl" style={{ maxHeight: 600, width: "calc(100dvw - 364px)" }}>
              <table className="table table-vcenter card-table">
                <thead>
                  <tr style={{ position: "sticky", top: 0, backgroundColor: "var(--tblr-card-bg)", zIndex: 1 }}>
                    {requestTableHeaders.map((header, index) => (
                      <th
                        className={`${header.narrow && "w-1"} ${header.wrap && "text-wrap"} ${
                          header.required && "header-required"
                        }`}
                        key={index}
                      >
                        {header.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {requestTable?.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <div
                          className={`relative align-items-center justify-content-center gap-3 w-8 h-8 ${
                            !isAdmin && budgetId ? "" : "d-flex"
                          }`}
                        >
                          {index + 1}
                          {!isAdmin && budgetId ? (
                            index + 1
                          ) : (
                            <button
                              type="button"
                              className="badge badge-notification badge-pill text-danger bg-danger-lt cursor-pointer"
                              style={{ padding: 2 }}
                              title="Удалить строку"
                              onClick={() => handleDeleteRow(index)}
                              disabled={isFormLoading}
                            >
                              <IconMinus size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                      <td>
                        <div title={item.article ? item.article : undefined} style={{ width: 300 }}>
                          <Select
                            classNamePrefix="react-select"
                            placeholder="Наименование"
                            value={item.article ? { label: item.article, value: item.article } : null}
                            onInputChange={(inputValue) => {
                              handleArticleInputChange(inputValue.slice(0, 100), index);
                              return inputValue;
                            }}
                            onChange={(newValue) => handleTableChange(index, "article", newValue ? newValue.value : "")}
                            options={(() => {
                              // Base options from search results
                              let options = articleOptions[index] || [];

                              // Add "Save" option when input exists but no results found
                              if (
                                articleInput[index]?.trim().length >= 3 &&
                                options.length === 0 &&
                                !articleLoading[index]
                              ) {
                                options = [
                                  {
                                    label: `Сохранить: \"${articleInput[index]}\"`,
                                    value: articleInput[index],
                                  },
                                ];
                              }

                              // Always include the currently selected option if it exists and isn't already in the list
                              if (item.article && !options.some((opt) => opt.value === item.article)) {
                                options = [{ label: item.article, value: item.article }, ...options];
                              }

                              return options;
                            })()}
                            isLoading={articleLoading[index]}
                            isDisabled={isFormLoading || (!isAdmin && !!budgetId)}
                            isClearable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            inputValue={articleInput[index]}
                            onBlur={() => {
                              // If user leaves field and nothing is selected, keep their input as value
                              if (
                                articleInput[index] &&
                                (!item.article || item.article !== articleInput[index]) &&
                                (articleOptions[index]?.length === 0 ||
                                  !articleOptions[index]?.some((opt) => opt.value === articleInput[index]))
                              ) {
                                handleTableChange(index, "article", articleInput[index]);
                              }
                            }}
                          />
                        </div>
                      </td>

                      {(mainLevel?.max_levels && mainLevel?.max_levels > 0
                        ? Array.from({ length: mainLevel.max_levels }, (_, i) => ({ num: i + 1 }))
                        : []
                      ).map((lvl, idx) => {
                        // For edit mode (when budgetId exists), use the stored budget_line_rows
                        const currentValue = requestTable[index].budget_line_rows[idx];

                        // Determine the current node options based on whether we're at first level or using children
                        const currentNode =
                          idx === 0
                            ? availableLevelsByRow[index]?.[0]
                            : requestTable[index].budget_line_rows[idx - 1]?.children || [];

                        // Create the current option object from the stored value
                        const currentOption = currentValue
                          ? {
                              value: currentValue.budget_type || "",
                              label: currentValue.budget_type || "",
                              disabled: !currentValue.status,
                            }
                          : null;

                        return (
                          <td key={idx}>
                            <div style={{ width: 240 }}>
                              <Select
                                classNamePrefix="react-select"
                                placeholder="Уровень"
                                value={currentOption}
                                onChange={(newValue) => {
                                  if (newValue) {
                                    const selectedNode = currentNode.find(
                                      (node) => node.budget_type === newValue.value
                                    );
                                    if (selectedNode) {
                                      handleTableChange(index, "budget_line_rows", selectedNode, lvl.num);
                                    }
                                  } else {
                                    handleTableChange(index, "budget_line_rows", null, lvl.num);
                                  }
                                }}
                                options={
                                  currentNode?.map((node) => ({
                                    value: node.budget_type || "",
                                    label: node.budget_type || "",
                                    disabled: !node.status,
                                  })) || []
                                }
                                isDisabled={isFormLoading || (!isAdmin && !!budgetId) || !currentNode?.length}
                                isOptionDisabled={(opt) => opt.disabled}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                }}
                              />
                            </div>
                          </td>
                        );
                      })}

                      <td>
                        <div style={{ width: 160 }}>
                          <Select
                            classNamePrefix="react-select"
                            name="code_it_id"
                            placeholder="Код ИТ"
                            value={codeOptions.find((opt) => opt.value === requestTable[index].code_it_id)}
                            onChange={(newValue) =>
                              handleTableChange(index, "code_it_id", newValue ? newValue.value : "")
                            }
                            isDisabled={isFormLoading || (!isAdmin && !!budgetId)}
                            options={codeOptions}
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </div>
                      </td>
                      <td>
                        <div style={{ width: 160 }}>
                          <Select
                            classNamePrefix="react-select"
                            name="contragent_id"
                            placeholder="Контрагент"
                            value={contractorOptions.find((opt) => opt.value === requestTable[index].contragent_id)}
                            onChange={(newValue) =>
                              handleTableChange(index, "contragent_id", newValue ? newValue.value : "")
                            }
                            isDisabled={isFormLoading || (!isAdmin && !!budgetId)}
                            options={contractorOptions}
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </div>
                      </td>
                      <td>
                        <div style={{ width: 88 }}>
                          <Select
                            classNamePrefix="react-select"
                            name="vat"
                            placeholder="Выберите вариант"
                            value={VAT_OPTIONS.find((opt) => String(opt.value) === String(requestTable[index].vat))}
                            onChange={(newValue) => handleTableChange(index, "vat", newValue ? newValue.value : "")}
                            isDisabled={isFormLoading || (!isAdmin && !!budgetId)}
                            options={VAT_OPTIONS}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </div>
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 72 }}
                          name="quantity"
                          value={item.quantity !== null && item.quantity !== undefined ? item.quantity : null}
                          onChange={(e) => handleTableChange(index, "quantity", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 120 }}
                          name="row_total"
                          value={item.row_total !== null && item.row_total !== undefined ? item.row_total : null}
                          onChange={(e) => handleTableChange(index, "row_total", e.target.value)}
                          disabled
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 120 }}
                          name="mon_1"
                          value={item.mon_1 !== null && item.mon_1 !== undefined ? item.mon_1 : null}
                          onChange={(e) => handleTableChange(index, "mon_1", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 120 }}
                          name="mon_2"
                          value={item.mon_2 !== null && item.mon_2 !== undefined ? item.mon_2 : null}
                          onChange={(e) => handleTableChange(index, "mon_2", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 120 }}
                          name="mon_3"
                          value={item.mon_3 !== null && item.mon_3 !== undefined ? item.mon_3 : null}
                          onChange={(e) => handleTableChange(index, "mon_3", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 120 }}
                          name="mon_4"
                          value={item.mon_4 !== null && item.mon_4 !== undefined ? item.mon_4 : null}
                          onChange={(e) => handleTableChange(index, "mon_4", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 120 }}
                          name="mon_5"
                          value={item.mon_5 !== null && item.mon_5 !== undefined ? item.mon_5 : null}
                          onChange={(e) => handleTableChange(index, "mon_5", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 120 }}
                          name="mon_6"
                          value={item.mon_6 !== null && item.mon_6 !== undefined ? item.mon_6 : null}
                          onChange={(e) => handleTableChange(index, "mon_6", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 120 }}
                          name="mon_7"
                          value={item.mon_7 !== null && item.mon_7 !== undefined ? item.mon_7 : null}
                          onChange={(e) => handleTableChange(index, "mon_7", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 120 }}
                          name="mon_8"
                          value={item.mon_8 !== null && item.mon_8 !== undefined ? item.mon_8 : null}
                          onChange={(e) => handleTableChange(index, "mon_8", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 120 }}
                          name="mon_9"
                          value={item.mon_9 !== null && item.mon_9 !== undefined ? item.mon_9 : null}
                          onChange={(e) => handleTableChange(index, "mon_9", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 120 }}
                          name="mon_10"
                          value={item.mon_10 !== null && item.mon_10 !== undefined ? item.mon_10 : null}
                          onChange={(e) => handleTableChange(index, "mon_10", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 120 }}
                          name="mon_11"
                          value={item.mon_11 !== null && item.mon_11 !== undefined ? item.mon_11 : null}
                          onChange={(e) => handleTableChange(index, "mon_11", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                      <td>
                        <FormattedNumberInput
                          className="form-control"
                          style={{ width: 120 }}
                          name="mon_12"
                          value={item.mon_12 !== null && item.mon_12 !== undefined ? item.mon_12 : null}
                          onChange={(e) => handleTableChange(index, "mon_12", e.target.value)}
                          disabled={isFormLoading || (!isAdmin && !!budgetId)}
                        />
                      </td>
                    </tr>
                  ))}

                  {/* Summary Row */}
                  <tr style={{ fontWeight: "bold", backgroundColor: "var(--tblr-bg-surface-secondary)" }}>
                    <td colSpan={1}>
                      <div className="text-center">Итого</div>
                    </td>
                    <td colSpan={mainLevel?.max_levels ? mainLevel.max_levels + 1 : 1}></td>
                    <td colSpan={3}></td>
                    <td>
                      <FormattedNumberInput
                        className="form-control fw-bold"
                        style={{ width: 72 }}
                        value={requestTable.reduce((sum, row) => sum + (row.quantity || 0), 0)}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control fw-bold"
                        style={{ width: 120 }}
                        value={requestTable.reduce((sum, row) => sum + (row.row_total || 0), 0)}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control fw-bold"
                        style={{ width: 120 }}
                        value={requestTable.reduce((sum, row) => sum + (row.mon_1 || 0), 0)}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control fw-bold"
                        style={{ width: 120 }}
                        value={requestTable.reduce((sum, row) => sum + (row.mon_2 || 0), 0)}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control fw-bold"
                        style={{ width: 120 }}
                        value={requestTable.reduce((sum, row) => sum + (row.mon_3 || 0), 0)}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control fw-bold"
                        style={{ width: 120 }}
                        value={requestTable.reduce((sum, row) => sum + (row.mon_4 || 0), 0)}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control fw-bold"
                        style={{ width: 120 }}
                        value={requestTable.reduce((sum, row) => sum + (row.mon_5 || 0), 0)}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control fw-bold"
                        style={{ width: 120 }}
                        value={requestTable.reduce((sum, row) => sum + (row.mon_6 || 0), 0)}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control fw-bold"
                        style={{ width: 120 }}
                        value={requestTable.reduce((sum, row) => sum + (row.mon_7 || 0), 0)}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control fw-bold"
                        style={{ width: 120 }}
                        value={requestTable.reduce((sum, row) => sum + (row.mon_8 || 0), 0)}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control fw-bold"
                        style={{ width: 120 }}
                        value={requestTable.reduce((sum, row) => sum + (row.mon_9 || 0), 0)}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control fw-bold"
                        style={{ width: 120 }}
                        value={requestTable.reduce((sum, row) => sum + (row.mon_10 || 0), 0)}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control fw-bold"
                        style={{ width: 120 }}
                        value={requestTable.reduce((sum, row) => sum + (row.mon_11 || 0), 0)}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control fw-bold"
                        style={{ width: 120 }}
                        value={requestTable.reduce((sum, row) => sum + (row.mon_12 || 0), 0)}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </form>
    </div>
  );

  const pageContent = (
    <div className="container-xl">
      {newBudgetBody}

      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="input-group" style={{ maxWidth: "300px" }}>
          <span className="input-group-text">Итого</span>
          <FormattedNumberInput
            className="form-control"
            style={{ width: 200 }}
            value={totalSum}
            onChange={(e) => undefined}
            disabled
          />
        </div>

        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate(-1)}
            disabled={isFormLoading}
          >
            Отмена
          </button>

          {budgetId && isAdmin && (
            <button
              className="btn btn-ghost-danger d-flex gap-1"
              data-bs-toggle="modal"
              data-bs-target="#modal-delete-budget"
              disabled={isFormLoading}
            >
              Удалить
              {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
            </button>
          )}

          {!isAdmin && budgetId ? undefined : (
            <button type="submit" form="budgetForm" className="btn btn-primary d-flex gap-1" disabled={isFormLoading}>
              {budgetId ? "Сохранить изменения" : "Создать бюджет"}
              {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <PageLayout
        children={pageContent}
        header={budgetId ? "Детали бюджета" : "Новый бюджет"}
        search={false}
        filters={false}
        sorters={false}
      />
      <DangerModal
        modal_id="modal-delete-budget"
        dangerDescription={deleteBudgetDanger}
        cancel={() => undefined}
        confirm={handleBudgetDelete}
      />
    </>
  );
}
